import * as forms from "@client/forms";
import * as typography from "@client/shared/typography";
import {useMessages, useSiteContext} from "@client/site/context";
import {ManagerLayout} from "@client/site/ManagerLayout";
import {classNames, reverse, server} from "@reactivated";
import React from "react";
import {
    BrowserRouter,
    Link,
    Route,
    Routes,
    StaticRouter,
    useNavigate,
} from "react-router";

// A form's text input holds "" once cleared.
const blank = (value: string | null) => value == null || value === "";

function ExerciseDetail(props: {
    exerciseType: server.manager.templates.ExerciseMedia["exercise_types"][number];
    onUpdate: (
        updated: server.manager.templates.ExerciseMedia["exercise_types"][number],
    ) => void;
}) {
    const navigate = useNavigate();
    const messages = useMessages();
    const {exerciseType} = props;
    const [isLoading, setIsLoading] = React.useState<null | "query" | "upload">(null);
    const [urls, setUrls] = React.useState<Array<string[]>>([]);
    const [codeLookUp, setCodeLookUp] = React.useState(false);
    const lookupForm = forms.useForm(server.journal.workouts.ExerciseMediaLookup);
    const form = forms.useForm(server.journal.workouts.ExerciseMedia);
    const media = form.values;
    const noGif = forms.useField(
        {kind: "boolean", name: "no_gif", label: "No gif"},
        {initial: false},
    );
    const noVideo = forms.useField(
        {kind: "boolean", name: "no_video", label: "No video"},
        {initial: false},
    );

    const imageUrls = urls.filter(
        ([url]) =>
            url.endsWith(".png") && url.includes(codeLookUp == true ? "max" : "small"),
    );
    const gifUrls =
        codeLookUp === true
            ? urls.filter(([url]) => url.endsWith(".gif") && url.includes("1080"))
            : [];

    const videoUrls =
        codeLookUp == true
            ? urls.filter(
                  ([url]) => url.endsWith(".mp4") && url.includes("GREEN") == false,
              )
            : [];

    const greenVideoUrls =
        codeLookUp == true
            ? urls.filter(
                  ([url]) => url.endsWith(".mp4") && url.includes("GREEN") == true,
              )
            : [];

    return (
        <div className="flex gap-4">
            <div className="flex-1 space-y-4">
                <h1 className="text-xl">{exerciseType.name}</h1>
                <form
                    className="space-y-3"
                    onSubmit={async (event) => {
                        event.preventDefault();
                        setIsLoading("query");
                        try {
                            const response = await forms.submitForm(
                                lookupForm,
                                server.journal.workouts.exercise_media_lookup,
                            );
                            if (response?.type === "success") {
                                setCodeLookUp(
                                    Number.isNaN(parseInt(lookupForm.values.query)) ===
                                        false,
                                );
                                setUrls(response.data.urls);
                            }
                        } finally {
                            setIsLoading(null);
                        }
                    }}
                >
                    <forms.Field field={lookupForm.fields.query} />
                    <typography.Button submit disabled={isLoading != null}>
                        {isLoading == "query" ? "Querying..." : "Submit"}
                    </typography.Button>
                </form>

                <form
                    className="space-y-3"
                    onSubmit={async (event) => {
                        event.preventDefault();

                        setIsLoading("upload");
                        try {
                            const response = await forms.submitForm(form, (data) =>
                                server.journal.workouts.update_exercise_media({
                                    ...data,
                                    exercise_type: {uuid: exerciseType.uuid},
                                }),
                            );
                            if (response?.type !== "success") return;
                            messages.success("Uploaded");
                            props.onUpdate({
                                ...exerciseType,
                                media: {uuid: crypto.randomUUID()},
                            });
                            void navigate("/");
                        } finally {
                            setIsLoading(null);
                        }
                    }}
                >
                    <forms.Field field={form.fields.gym_visual_image_path} />
                    <forms.Field field={form.fields.gym_visual_gif_path} />
                    <forms.Field field={form.fields.gym_visual_video_path} />
                    <forms.Field field={form.fields.gym_visual_green_video_path} />
                    <forms.Field field={form.fields.gym_visual_code} />
                    <forms.Field field={form.fields.gym_visual_side} />
                    <forms.Field field={noGif} />
                    <forms.Field field={noVideo} />
                    <typography.Button
                        submit
                        disabled={
                            isLoading != null ||
                            blank(media.gym_visual_gif_path) ||
                            (noGif.value == false &&
                                blank(media.gym_visual_image_path)) ||
                            (noVideo.value == false &&
                                (blank(media.gym_visual_video_path) ||
                                    blank(media.gym_visual_green_video_path))) ||
                            blank(media.gym_visual_code)
                        }
                    >
                        {isLoading == "upload" ? "Uploading..." : "Upload"}
                    </typography.Button>
                </form>
            </div>
            <div className="h-[80vh] flex-1 space-y-2 overflow-y-scroll">
                <h2 className="text-lg">Images</h2>
                <div className="grid grid-cols-4 gap-2">
                    {imageUrls.map(([url, public_url]) => (
                        <img
                            onClick={() =>
                                form.fields.gym_visual_image_path.onChange(url)
                            }
                            key={url}
                            className={classNames(
                                "cursor-pointer border-solid border-2",
                                media.gym_visual_image_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                            src={public_url}
                        />
                    ))}
                </div>
                <h2 className="text-lg">Gifs</h2>

                <div className="grid grid-cols-4">
                    {gifUrls.map(([url, public_url]) => (
                        <img
                            onClick={() =>
                                form.fields.gym_visual_gif_path.onChange(url)
                            }
                            key={url}
                            className={classNames(
                                "cursor-pointer border-solid border-2",
                                media.gym_visual_gif_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                            src={public_url}
                        />
                    ))}
                </div>

                <h2 className="text-lg">Videos</h2>

                <div className="grid grid-cols-4">
                    {videoUrls.map(([url, public_url]) => (
                        <div
                            key={url}
                            className={classNames(
                                "relative border-solid border-2",
                                media.gym_visual_video_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                        >
                            <video autoPlay src={public_url} controls />
                            <div
                                onClick={() =>
                                    form.fields.gym_visual_video_path.onChange(url)
                                }
                                className="absolute inset-0 z-10 cursor-pointer"
                            ></div>
                        </div>
                    ))}
                </div>
                <h2 className="text-lg">Green</h2>
                <div className="grid grid-cols-3">
                    {greenVideoUrls.map(([url, public_url]) => (
                        <div
                            key={url}
                            className={classNames(
                                "relative border-solid border-2",
                                media.gym_visual_green_video_path == url
                                    ? "border-red-600"
                                    : "border-indigo-600",
                            )}
                        >
                            <video autoPlay src={public_url} controls />
                            <div
                                onClick={() =>
                                    form.fields.gym_visual_green_video_path.onChange(
                                        url,
                                    )
                                }
                                className="absolute inset-0 z-10 cursor-pointer"
                            ></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
const Router = import.meta.env.SSR == true ? StaticRouter : BrowserRouter;

export const Template = (props: server.manager.templates.ExerciseMedia) => {
    const {request} = useSiteContext();
    const [exerciseTypes, setExerciseTypes] = React.useState(props.exercise_types);

    const basename = reverse("exercise_media");
    const index = (
        <div className="space-y-2">
            <table className="table-fixed">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Count</th>
                        <th>Media</th>
                    </tr>
                </thead>
                <tbody>
                    {exerciseTypes.map((exerciseType) => (
                        <tr key={exerciseType.uuid}>
                            <td>
                                <Link
                                    to={`${exerciseType.uuid}/`}
                                    className="flex gap-2"
                                >
                                    {exerciseType.name}
                                    <div></div>
                                </Link>
                            </td>

                            <td>{exerciseType.instances_count}</td>
                            <td>
                                {exerciseType.media != null && <strong>Yes</strong>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <Router location={request.path} basename={basename}>
            <ManagerLayout title="Exercise media" currentClientManagerId={null}>
                <Routes>
                    <Route path="/" element={index} />
                    {exerciseTypes.map((exerciseType) => (
                        <Route
                            key={exerciseType.uuid}
                            path={`${exerciseType.uuid}/`}
                            element={
                                <ExerciseDetail
                                    exerciseType={exerciseType}
                                    onUpdate={(updated) =>
                                        setExerciseTypes((prevState) =>
                                            prevState.map((item) =>
                                                item.uuid === exerciseType.uuid
                                                    ? updated
                                                    : item,
                                            ),
                                        )
                                    }
                                />
                            }
                        />
                    ))}
                    <Route path="*" element={<div>Not found</div>} />
                </Routes>
            </ManagerLayout>
        </Router>
    );
};
